import { Alert, Box, Button, Field, Flex, Input, Stack, Text } from '@chakra-ui/react'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { ActionBar } from '~/components/member/ActionBar'
import { DeviceLocationCard, type DeviceAnswer } from '~/components/member/DeviceLocationCard'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router'
import { api, isApiError } from '~/api/client'
import { useFetchRegistration, usePlaces } from '~/api/hooks'
import { FlowStepper } from '~/components/member/FlowStepper'
import { WhyLink } from '~/components/member/WhyLink'
import { ErrorAlert, useFriendlyError } from '~/components/member/feedback'
import { FormStack, MemberLayout } from '~/components/member/layout'
import { useT } from '~/i18n/use-t'
import { clearDraft, readDraft, saveDraft } from '~/lib/draft'
import { countryName } from '~/lib/country'
import { pathForNext, pathForStatus } from '~/lib/flow'
import { useStepRegistration } from '~/lib/use-step'
import { useDebounced } from '~/lib/use-debounced'

const FORM_ID = 'place-form'

interface Chosen {
  id: number
  name: string
  admin1: string | null
  country: string
}

const label = (p: Chosen) => `${p.name}, ${countryName(p.country)}`

/**
 * S3 Where do you live? (Step n of N), two short screens of one step. First the city search with its "Why?" (the
 * GeoNames credit is in that FAQ answer); Continue only checks a place is chosen. Then (`?part=location`) the device
 * location card for the chosen place (location v2, R111a: required, strict or optional per the admin policy), whose
 * Continue saves the place. The how-to notes are in the FAQ.
 */
export default function WhereYouLive() {
  const { t } = useT()
  const friendly = useFriendlyError()
  const navigate = useNavigate()
  const location = useLocation()
  const [params] = useSearchParams()
  const { providerId = '' } = useParams()
  const step = useStepRegistration('place', 'place')
  const refetchRegistration = useFetchRegistration()
  const draft = readDraft()
  const [query, setQuery] = useState(draft.cityQuery ?? '')
  const [selected, setSelected] = useState<Chosen | null>(draft.city ?? null)
  const [note, setNote] = useState(draft.note ?? '')
  const [showNote, setShowNote] = useState(Boolean(draft.note))
  const [active, setActive] = useState(-1)
  // The list is hidden after Escape or a blur until the member types again.
  const [dismissed, setDismissed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The device answer for the chosen place (location v2); a new place needs a new answer.
  const [answer, setAnswer] = useState<DeviceAnswer>(null)
  const [answerFor, setAnswerFor] = useState<number | null>(null)
  const [locError, setLocError] = useState<string | null>(null)
  // Bumped to remount the location card idle (gate audit 1: a 409 device_location_required after the fix aged out).
  const [cardKey, setCardKey] = useState(0)
  const restored = useRef(false)

  // A city saved earlier (a return to this step) is shown again.
  useEffect(() => {
    const city = step.reg?.city
    if (restored.current || !city || readDraft().city) return
    restored.current = true
    const chosen = { id: city.id, name: city.name, admin1: city.admin1, country: city.country }
    setSelected(chosen)
    setQuery(label(city))
    saveDraft({ cityQuery: label(city), city: chosen })
    if (city.note) {
      setNote(city.note)
      setShowNote(true)
    }
  }, [step.reg?.city])

  // The second screen needs a chosen place: a link to it without one (or one the restore above is about to fill)
  // shows the first screen instead.
  const wantsLocation = params.get('part') === 'location'
  const atLocation = wantsLocation && selected !== null
  const restoring = !restored.current && Boolean(step.reg?.city) && !readDraft().city
  useEffect(() => {
    if (wantsLocation && step.ok && !selected && !restoring) navigate(location.pathname, { replace: true })
  }, [wantsLocation, step.ok, selected, restoring, location.pathname, navigate])

  const debounced = useDebounced(query.trim(), 250)
  const searching = !selected && query.trim().length >= 2
  const places = usePlaces(searching ? debounced : '')
  const items = searching && debounced.length >= 2 ? (places.data?.items ?? []).slice(0, 8) : []
  const listOpen = items.length > 0 && !dismissed

  // The confirmation of S2 is shown once: take it out of the history entry so a reload or a Back does not repeat it.
  const confirmed = (location.state as { confirmed?: boolean } | null)?.confirmed === true
  useEffect(() => {
    if (confirmed) navigate(location.pathname, { replace: true, state: null })
  }, [confirmed, location.pathname, navigate])
  const [showConfirmed] = useState(confirmed)

  const save = useMutation({
    mutationFn: async () => {
      await api('registrationCity', { body: { placeId: selected!.id, note: note.trim() || undefined } })
      return refetchRegistration()
    },
    onSuccess: (r) => {
      clearDraft('cityQuery', 'city', 'note', 'name', 'email', 'resendAt')
      // Spec §12.1 step 6: a member the policy exempts is shown "No document needed" once, on the way to the face check.
      navigate(
        r.document?.applies && r.document.state === 'exempt' ? pathForNext(r.provider, 'document') : pathForStatus(r)
      )
    },
    onError: (err) => {
      // The server no longer counts the answer (the fix is older than 10 minutes, or the policy changed): forget it,
      // reload the registration and show the card again from the start so the person can share again.
      if (!isApiError(err, 'device_location_required') || !selected) return
      setAnswer(null)
      setAnswerFor(selected.id)
      setCardKey((k) => k + 1)
      void refetchRegistration()
    },
  })

  if (!step.ok) return step.gate
  const reg = step.reg

  const choose = (p: Chosen) => {
    setSelected(p)
    setQuery(label(p))
    saveDraft({ cityQuery: label(p), city: p })
    setActive(-1)
    setDismissed(false)
    setError(null)
  }

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!listOpen) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => (a + 1) % items.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => (a <= 0 ? items.length - 1 : a - 1))
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault()
      choose(items[active]!)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setActive(-1)
      setDismissed(true)
    }
  }

  const info = reg.location
  // A fresh fix for this place (or a refusal) the server already has counts after a reload.
  const serverAnswer: DeviceAnswer = selected
    ? info.fixPlaceId === selected.id
      ? 'fix'
      : info.refused
        ? 'refused'
        : null
    : null
  const currentAnswer = selected && answerFor === selected.id ? answer : serverAnswer

  // First screen: only checks a place is chosen (nothing is saved); the location screen is its own history entry.
  const next = (e: FormEvent) => {
    e.preventDefault()
    if (!selected) {
      setError(t('place.errChoose'))
      return
    }
    setError(null)
    setLocError(null)
    save.reset()
    navigate(`${location.pathname}?part=location`)
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!selected) return
    if (info.mode !== 'optional' && currentAnswer === null) {
      setLocError(t(info.mode === 'required' ? 'place.errLocationOrCant' : 'place.errLocation'))
      return
    }
    setLocError(null)
    save.mutate()
  }

  return (
    <MemberLayout flow backTo={atLocation ? location.pathname : `/join/${providerId}`} backHistory={atLocation}>
      <FlowStepper step='place' />
      {showConfirmed && !atLocation && (
        <Alert.Root status='success' role='status' borderRadius='md'>
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Description>{t('place.confirmed')}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      )}
      {/* Keyed per screen: the pressed Continue leaves with its screen, so the new screen's heading takes the focus. */}
      <FormStack
        key={atLocation ? 'location' : 'place'}
        id={FORM_ID}
        gap={2}
        onSubmit={atLocation ? submit : next}
        noValidate
      >
        {!atLocation && (
          <>
            <Stack gap={1}>
              <Field.Root invalid={Boolean(error)}>
                <Flex align='center' gap={2}>
                  <Field.Label>{t('place.label')}</Field.Label>
                  {/* Inline: its 48 px hit box overhangs the row, so the label row stays one line of text tall. */}
                  <WhyLink faq='place' inline />
                </Flex>
                <Input
                  role='combobox'
                  aria-expanded={listOpen}
                  aria-controls='place-list'
                  aria-autocomplete='list'
                  aria-activedescendant={active >= 0 ? `place-opt-${active}` : undefined}
                  autoComplete='off'
                  enterKeyHint='search'
                  value={query}
                  onKeyDown={onKey}
                  onBlur={() => setDismissed(true)}
                  onFocus={() => setDismissed(false)}
                  onChange={(e) => {
                    setQuery(e.target.value)
                    setSelected(null)
                    setActive(-1)
                    setDismissed(false)
                    setError(null)
                    saveDraft({ cityQuery: e.target.value })
                    clearDraft('city')
                  }}
                />
                {/* Once a place is chosen its name is in the field: the hint has done its job. */}
                {!selected && <Field.HelperText>{t('place.hint')}</Field.HelperText>}
                <Field.ErrorText>{error}</Field.ErrorText>
              </Field.Root>
              {listOpen && (
                <Box
                  as='ul'
                  id='place-list'
                  role='listbox'
                  aria-label={t('place.listLabel')}
                  listStyleType='none'
                  m={0}
                  p={0}
                  borderWidth='1px'
                  borderColor='border'
                  borderRadius='md'
                  overflow='hidden'
                >
                  {items.map((p, i) => (
                    <Box
                      as='li'
                      key={p.id}
                      id={`place-opt-${i}`}
                      role='option'
                      aria-selected={i === active}
                      minH='12'
                      px={4}
                      py={2}
                      display='flex'
                      alignItems='center'
                      flexWrap='wrap'
                      gap={2}
                      cursor='pointer'
                      bg={i === active ? 'bg.muted' : undefined}
                      _hover={{ bg: 'bg.muted' }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => choose(p)}
                    >
                      <span>{label(p)}</span>
                      {p.admin1 && (
                        <Text as='small' color='fg.muted' fontSize='md'>
                          ({p.admin1})
                        </Text>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
              {searching && places.isFetching && (
                <Text role='status' color='fg.muted'>
                  {t('place.searching')}
                </Text>
              )}
              {searching && !places.isFetching && debounced.length >= 2 && places.data && items.length === 0 && (
                <Text role='status'>{t('place.noResults')}</Text>
              )}
            </Stack>

            {showNote ? (
              <Field.Root>
                <Text mb={2}>{t('place.notInListText')}</Text>
                <Field.Label>{t('place.noteLabel')}</Field.Label>
                <Input
                  value={note}
                  maxLength={200}
                  onChange={(e) => {
                    setNote(e.target.value)
                    saveDraft({ note: e.target.value })
                  }}
                />
              </Field.Root>
            ) : (
              <WhyLink
                label={t('place.notInList')}
                color='fg'
                alignSelf='flex-start'
                onClick={() => setShowNote(true)}
              />
            )}
          </>
        )}

        {/* The location purpose and mode lines stay in view (U11 d); the how-to notes are in the FAQ. */}
        {atLocation && selected && (
          <>
            <Text fontWeight='bold'>{label(selected)}</Text>
            <DeviceLocationCard
              key={`${selected.id}:${cardKey}`}
              placeId={selected.id}
              info={info}
              initial={currentAnswer}
              onAnswer={(a) => {
                setAnswer(a)
                setAnswerFor(selected.id)
                if (a) setLocError(null)
              }}
            />
            {locError && <ErrorAlert>{locError}</ErrorAlert>}

            {save.isError && (
              <ErrorAlert>
                {isApiError(save.error, 'device_location_required') ? t('place.errLocation') : friendly(save.error)}
              </ErrorAlert>
            )}
          </>
        )}
        <ActionBar>
          <Button type='submit' form={FORM_ID} size='lg' w='full' loading={save.isPending}>
            {t('place.continue')}
          </Button>
        </ActionBar>
      </FormStack>
    </MemberLayout>
  )
}
